import { useRef } from 'react';
import { Button } from '@/components/ui/Button';
import type { IndividualDetail } from './api';
import type { PortfolioRow } from './portfolioApi';
import type { AssessmentListItem } from '@/features/assessments/api';
import type { IndividualIntensiveClass } from '@lentera/shared';

interface PelitaCvModalProps {
  open: boolean;
  onClose: () => void;
  individual: IndividualDetail;
  portfolios: PortfolioRow[];
  assessments: AssessmentListItem[];
  intensiveClass?: IndividualIntensiveClass | null;
}

export function PelitaCvModal({
  open,
  onClose,
  individual,
  portfolios,
  assessments,
  intensiveClass,
}: PelitaCvModalProps) {
  const printAreaRef = useRef<HTMLDivElement>(null);

  if (!open) return null;

  const handlePrint = () => {
    window.print();
  };

  const prestasiList = portfolios.filter((p) => p.category === 'prestasi');
  const karyaList = portfolios.filter((p) => p.category === 'karya');
  const publikasiList = portfolios.filter((p) => p.category === 'publikasi');

  const regionText = individual.regionPath?.map((r) => r.name).join(', ') || individual.regionName || '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4 sm:p-6 backdrop-blur-xs">
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl overflow-hidden print:max-h-none print:shadow-none print:m-0 print:p-0 print:border-none print:w-full">
        {/* Modal Toolbar (hidden when printing) */}
        <div className="no-print flex items-center justify-between border-b border-border bg-subtle px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">📄</span>
            <h3 className="text-lg font-bold text-ink">Curriculum Vitae (CV) PELITA SLI</h3>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onClose}>
              Tutup
            </Button>
            <Button onClick={handlePrint}>
              🖨️ Cetak / Simpan PDF
            </Button>
          </div>
        </div>

        {/* Printable CV Container */}
        <div
          ref={printAreaRef}
          className="printable-cv flex-1 overflow-y-auto p-8 sm:p-12 text-ink print:overflow-visible print:p-0 print:text-black"
        >
          {/* Header CV */}
          <div className="border-b-2 border-primary pb-6 mb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left">
                {individual.photoKey ? (
                  <img
                    src={individual.photoKey.startsWith('http') ? individual.photoKey : `/api/v1/files/${individual.photoKey}`}
                    alt={individual.name}
                    className="size-28 rounded-full object-cover border-2 border-primary shadow-sm"
                  />
                ) : (
                  <div className="size-28 rounded-full bg-primary-soft text-primary font-bold text-3xl flex items-center justify-center border-2 border-primary shadow-sm">
                    {individual.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="inline-block px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-primary text-white mb-2">
                    {individual.code}
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-ink tracking-tight">
                    {individual.name}
                  </h1>
                  {individual.personalBranding && (
                    <p className="mt-1 text-base font-medium text-primary">
                      {individual.personalBranding}
                    </p>
                  )}
                  {individual.profession && (
                    <p className="text-sm text-ink-secondary">
                      {individual.profession}
                      {individual.lastEducation && ` · ${individual.lastEducation}`}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right text-xs text-ink-secondary hidden sm:block border-l pl-4 border-border">
                <div className="font-bold text-ink text-sm">PELITA SLI</div>
                <div>Sekolah Literasi Indonesia</div>
                <div className="text-ink-muted mt-1">Dicetak: {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Left Column: Data Diri & Kontak */}
            <div className="space-y-6 md:border-r md:border-border md:pr-6">
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-primary border-b border-border pb-1.5 mb-3">
                  Informasi Pribadi
                </h4>
                <dl className="space-y-2 text-xs">
                  <div>
                    <dt className="text-ink-muted">Jenis Kelamin</dt>
                    <dd className="font-medium text-ink">
                      {individual.gender === 'L' ? 'Laki-laki' : individual.gender === 'P' ? 'Perempuan' : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink-muted">Tempat, Tanggal Lahir</dt>
                    <dd className="font-medium text-ink">
                      {[individual.birthPlace, individual.birthDate].filter(Boolean).join(', ') || '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink-muted">Pendidikan Terakhir</dt>
                    <dd className="font-medium text-ink">{individual.lastEducation || '—'}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-primary border-b border-border pb-1.5 mb-3">
                  Basis Komunitas & Wilayah
                </h4>
                <dl className="space-y-2 text-xs">
                  <div>
                    <dt className="text-ink-muted">TBM Induk</dt>
                    <dd className="font-medium text-ink">
                      {individual.tbmName ? `${individual.tbmCode ? `${individual.tbmCode} - ` : ''}${individual.tbmName}` : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink-muted">Wilayah</dt>
                    <dd className="font-medium text-ink">{regionText}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-primary border-b border-border pb-1.5 mb-3">
                  Kontak
                </h4>
                <dl className="space-y-2 text-xs">
                  {individual.phone && (
                    <div>
                      <dt className="text-ink-muted">No. Telepon / WhatsApp</dt>
                      <dd className="font-medium text-ink">{individual.phone}</dd>
                    </div>
                  )}
                  {individual.email && (
                    <div>
                      <dt className="text-ink-muted">Email</dt>
                      <dd className="font-medium text-ink">{individual.email}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>

            {/* Right Column: Track Record Kompetensi & Portofolio */}
            <div className="md:col-span-2 space-y-7">
              {/* Program Kapasitas: Kelas Intensif */}
              {intensiveClass && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-primary border-b border-border pb-1.5 mb-3">
                    Pengembangan Kapasitas: Kelas Intensif PELITA SLI (10 Sesi Aktivitas)
                  </h4>
                  <div className="rounded-md border border-border bg-subtle/40 p-3.5 space-y-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2.5">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-xs text-ink">Status Capaian:</span>
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-primary text-white">
                          {intensiveClass.status === 'lulus' ? 'Lulus Pelatihan' : intensiveClass.status}
                        </span>
                      </div>
                      <div className="text-xs font-medium text-ink-secondary">
                        Kehadiran: <strong className="text-ink tabular">{intensiveClass.attendancePct}%</strong> (10 Sesi)
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="rounded bg-white p-2 border border-border">
                        <div className="text-[10px] text-ink-muted uppercase">Pre-Test</div>
                        <div className="font-bold tabular text-ink mt-0.5">
                          {intensiveClass.preTestScore !== null && intensiveClass.preTestScore !== undefined
                            ? intensiveClass.preTestScore
                            : '—'}
                        </div>
                      </div>
                      <div className="rounded bg-white p-2 border border-border">
                        <div className="text-[10px] text-primary uppercase font-semibold">Post-Test</div>
                        <div className="font-extrabold tabular text-primary mt-0.5">
                          {intensiveClass.postTestScore !== null && intensiveClass.postTestScore !== undefined
                            ? intensiveClass.postTestScore
                            : '—'}
                        </div>
                      </div>
                      <div className="rounded bg-white p-2 border border-border">
                        <div className="text-[10px] text-emerald-600 uppercase font-semibold">Gain Score</div>
                        <div className="font-extrabold tabular text-emerald-700 mt-0.5">
                          {intensiveClass.gainScore !== null && intensiveClass.gainScore !== undefined
                            ? intensiveClass.gainScore > 0
                              ? `+${intensiveClass.gainScore}`
                              : `${intensiveClass.gainScore}`
                            : '—'}
                        </div>
                      </div>
                    </div>
                    {/* Rincian Evaluasi 10 Sesi (jika tersedia) */}
                    {intensiveClass.sessionScores && intensiveClass.sessionScores.length > 0 && (
                      <div className="pt-2 border-t border-border/70">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-secondary mb-1.5">
                          Rincian Nilai Per Sesi:
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 text-[10px]">
                          {intensiveClass.sessionScores.map((sc) => (
                            <div
                              key={sc.sessionNumber}
                              className="rounded bg-white p-1.5 border border-border/80 text-center shadow-2xs"
                            >
                              <div className="font-bold text-ink">Sesi #{sc.sessionNumber}</div>
                              <div className="tabular text-ink-muted mt-0.5">
                                {sc.preTest ?? '—'} ➔ <strong className="text-primary">{sc.postTest ?? '—'}</strong>
                              </div>
                              {sc.gain !== null && sc.gain !== undefined && (
                                <div
                                  className={`font-bold text-[9px] mt-0.5 ${
                                    sc.gain > 0
                                      ? 'text-emerald-700'
                                      : sc.gain < 0
                                        ? 'text-rose-700'
                                        : 'text-gray-600'
                                  }`}
                                >
                                  {sc.gain > 0 ? `+${sc.gain}` : sc.gain}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {intensiveClass.notes && (
                      <p className="text-[11px] text-ink-secondary italic pt-1">
                        &ldquo;{intensiveClass.notes}&rdquo;
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Riwayat Penilaian Kompetensi */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-primary border-b border-border pb-1.5 mb-3">
                  Track Record Penilaian Kompetensi
                </h4>
                {assessments.length === 0 ? (
                  <p className="text-xs text-ink-muted italic">Belum ada riwayat penilaian kompetensi.</p>
                ) : (
                  <div className="overflow-hidden rounded-md border border-border">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-subtle text-ink-secondary border-b border-border font-semibold">
                        <tr>
                          <th className="px-3 py-2">Periode / Semester</th>
                          <th className="px-3 py-2">Indeks</th>
                          <th className="px-3 py-2">Kategori Capaian</th>
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {assessments.map((a) => (
                          <tr key={a.id} className="hover:bg-subtle/50">
                            <td className="px-3 py-2 font-medium">{a.periodName}</td>
                            <td className="px-3 py-2 font-bold tabular">
                              {a.totalScore !== null ? Math.round(a.totalScore * 100) / 100 : '—'}
                            </td>
                            <td className="px-3 py-2 font-semibold text-primary">{a.band || '—'}</td>
                            <td className="px-3 py-2 uppercase text-[10px] font-bold text-ink-muted">
                              {a.status}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Portofolio: Prestasi */}
              <div>
                <div className="flex items-center justify-between border-b border-border pb-1.5 mb-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-primary">
                    Prestasi & Rekognisi (Lomba / Kompetisi / Organisasi)
                  </h4>
                  <span className="text-[11px] text-ink-muted font-medium">({prestasiList.length})</span>
                </div>
                {prestasiList.length === 0 ? (
                  <p className="text-xs text-ink-muted italic">Belum ada data prestasi yang ditambahkan.</p>
                ) : (
                  <div className="space-y-2.5">
                    {prestasiList.map((item) => (
                      <div key={item.id} className="rounded-md border border-border p-3 text-xs bg-subtle/20">
                        <div className="flex items-start justify-between gap-2">
                          <h5 className="font-bold text-ink text-sm">{item.title}</h5>
                          {item.dateOrYear && (
                            <span className="shrink-0 px-2 py-0.5 rounded bg-primary-soft text-primary font-semibold text-[11px]">
                              {item.dateOrYear}
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className="mt-1 text-ink-secondary text-xs leading-relaxed">{item.description}</p>
                        )}
                        {item.linkUrl && (
                          <a
                            href={item.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1.5 inline-block text-[11px] text-primary hover:underline"
                          >
                            🔗 Tautan Dokumen / Bukti
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Portofolio: Karya */}
              <div>
                <div className="flex items-center justify-between border-b border-border pb-1.5 mb-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-primary">
                    Karya Literasi (Buku / Lagu / Puisi / dsb)
                  </h4>
                  <span className="text-[11px] text-ink-muted font-medium">({karyaList.length})</span>
                </div>
                {karyaList.length === 0 ? (
                  <p className="text-xs text-ink-muted italic">Belum ada karya yang ditambahkan.</p>
                ) : (
                  <div className="space-y-2.5">
                    {karyaList.map((item) => (
                      <div key={item.id} className="rounded-md border border-border p-3 text-xs bg-subtle/20">
                        <div className="flex items-start justify-between gap-2">
                          <h5 className="font-bold text-ink text-sm">{item.title}</h5>
                          {item.dateOrYear && (
                            <span className="shrink-0 px-2 py-0.5 rounded bg-primary-soft text-primary font-semibold text-[11px]">
                              {item.dateOrYear}
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className="mt-1 text-ink-secondary text-xs leading-relaxed">{item.description}</p>
                        )}
                        {item.linkUrl && (
                          <a
                            href={item.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1.5 inline-block text-[11px] text-primary hover:underline"
                          >
                            🔗 Tautan Karya
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Portofolio: Publikasi */}
              <div>
                <div className="flex items-center justify-between border-b border-border pb-1.5 mb-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-primary">
                    Publikasi & Media (Konten IG / TikTok / YouTube / Media Cetak & Daring)
                  </h4>
                  <span className="text-[11px] text-ink-muted font-medium">({publikasiList.length})</span>
                </div>
                {publikasiList.length === 0 ? (
                  <p className="text-xs text-ink-muted italic">Belum ada publikasi yang ditambahkan.</p>
                ) : (
                  <div className="space-y-2.5">
                    {publikasiList.map((item) => (
                      <div key={item.id} className="rounded-md border border-border p-3 text-xs bg-subtle/20">
                        <div className="flex items-start justify-between gap-2">
                          <h5 className="font-bold text-ink text-sm">{item.title}</h5>
                          {item.dateOrYear && (
                            <span className="shrink-0 px-2 py-0.5 rounded bg-primary-soft text-primary font-semibold text-[11px]">
                              {item.dateOrYear}
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className="mt-1 text-ink-secondary text-xs leading-relaxed">{item.description}</p>
                        )}
                        {item.linkUrl && (
                          <a
                            href={item.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1.5 inline-block text-[11px] text-primary hover:underline font-medium"
                          >
                            🔗 Buka Publikasi ({item.linkUrl})
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
